import React, {
  useEffect,
  useState,
  useMemo,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import isEmpty from 'lodash/isEmpty';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Label,
  Table,
  Spinner,
  Button,
} from 'reactstrap';
import Select from 'react-select';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import Timeline from '../Dashboard/timeline';
import { useMemberCompanyTimezones } from '../../hooks/useMemberCompanyTimezones';
import memberSessionsService from '../../services/memberSessions';
import { getUserTimezone } from '../../utils/timezone';
import { usePrevious } from '../../hooks/userPrevious';
import { fetchApplicationInfoMapping } from '../../services/applicationInfo';
import { getAppNameByAppPath } from '@utils/utility';
import '../../assets/scss/argon-dashboard/custom/body-container/_body-container.scss';
import { memberActionTypes } from '@store/Member/Member.actionTypes';
import { useTeamMemberDetailsForSelect } from '../../hooks/useTeamMemberDetailsForSelect';
import { EmptyState } from '@/components/EmptyState';
import {
  useUrlDateRangeSync,
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlTimezoneIgnoreFlagSync,
  useUrlFilterWrite,
  getDefaultMemberFromOptions,
} from '@/hooks/useUrlFilterSync';
import {
  getInitialReportDateRangeFromSearch,
  getReportFiltersFromSearch,
} from '@/utils/reportFiltersUrl';
const UsersDataPage = () => {
  const dispatch = useDispatch();
  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);

  const { activeCompany } = useSelector((state) => state.auth.profile);
  const activeCompanyId = activeCompany?.companyId;
  const teamId = team?.active?.activeTeamid;
  const profileID = auth.profile?.id;

  const {
    memberDetails,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useTeamMemberDetailsForSelect(teamId);

  const membersOptions = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .map(([uid, m]) => ({
        value: uid,
        label: m?.employeeId
          ? `${m?.userName || m?.userEmailid || uid} (${m.employeeId})`
          : m?.userName || m?.userEmailid || uid,
        userUID: uid,
        employeeId: m?.employeeId,
      }))
      .filter((o) => o.value)
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [memberDetails]);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const location = useLocation();
  const history = useHistory();

  // State — dateRange seeds from the URL (or today if absent/invalid) so
  // refresh, browser back/forward, and links from other pages restore the
  // same filters instead of resetting.
  const [dateRange, setDateRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [appUsage, setAppUsage] = useState({});
  const [loadingAppUsage, setLoadingAppUsage] = useState(false);
  const [sessionsData, setSessionsData] = useState(null);
  const [rate, setRate] = useState('');
  const [appNameMapping, setAppNameMapping] = useState({});
  // Make all selects/inputs consistent height and spacing
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      height: '38px',
      cursor: 'pointer',
    }),
    valueContainer: (base) => ({
      ...base,
      height: '38px',
      padding: '0 8px',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '38px',
      cursor: 'pointer',
    }),
  };

  // Fetch application info mapping on mount
  useEffect(() => {
    fetchApplicationInfoMapping().then((mapping) => {
      setAppNameMapping(mapping);
    });
  }, []);

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    return match?.employeeId ?? userId;
  };

  const effectiveEmployeeId = useMemo(() => {
    return getEmployeeIdForUser(selectedUserId || auth.profile?.id);
  }, [selectedUserId, membersOptions, auth.profile?.id]);

  // Seeds the timezone hook's `initialTimezone` from the URL, but only while
  // `ignoreUrlTimezone` is false — reset to true on a manual member/timezone
  // pick so the hook's own "auto-follow the newly selected member's own
  // timezone" effect can still take over afterwards (same pattern as
  // Timeline/AppUsage/Screenshots/UrlTracking).
  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);

  useUrlTimezoneIgnoreFlagSync({ history, setIgnoreUrlTimezone });

  const filtersFromUrl = useMemo(
    () => getReportFiltersFromSearch(location.search),
    [location.search]
  );
  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  // Use the custom hook for timezone management
  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId: selectedUserId || auth.profile?.id,
    companyId: activeCompanyId,
    initialTimezone: urlTimezoneForHook,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  // Fetch sessions and app usage using fetchTimeTrackingDataV2
  const fetchSessionsAndAppUsage = React.useCallback(async () => {
    console.log('🟢 [UsersData] fetchSessionsAndAppUsage called with:', {
      teamId,
      activeCompanyId,
      effectiveEmployeeId,
      dateRange,
      selectedTimezone,
      selectedUserId,
    });

    if (
      !teamId ||
      !activeCompanyId ||
      !effectiveEmployeeId ||
      !dateRange.start ||
      !dateRange.end
    ) {
      console.warn(
        '🟡 [UsersData] Missing required parameters, skipping fetch:',
        {
          hasTeamId: !!teamId,
          hasActiveCompanyId: !!activeCompanyId,
          hasEffectiveEmployeeId: !!effectiveEmployeeId,
          hasStartDate: !!dateRange.start,
          hasEndDate: !!dateRange.end,
        }
      );
      setSessionsData(null);
      setAppUsage({});
      // Clear memberTimeData in Redux when no data
      dispatch({
        type: memberActionTypes.SET_STATE,
        payload: {
          memberTimeData: null,
          memberLoading: false,
        },
      });
      return;
    }

    setLoadingAppUsage(true);
    // Set loading state in Redux for Timeline component
    dispatch({
      type: memberActionTypes.SET_STATE,
      payload: {
        memberLoading: true,
      },
    });

    try {
      // Get timezone for the user
      let timezone = selectedTimezone;
      if (!timezone && selectedUserId) {
        try {
          const tzInfo = await getUserTimezone(teamId, selectedUserId);
          timezone = tzInfo.timezone || moment.tz.guess();
          console.log('🟢 [UsersData] Fetched timezone:', timezone);
        } catch (tzError) {
          console.warn(`🟡 [UsersData] Failed to get timezone:`, tzError);
          timezone = moment.tz.guess();
        }
      }

      console.log('🟢 [UsersData] Calling fetchTimeTrackingDataV2 with:', {
        teamId,
        companyId: activeCompanyId,
        userId: selectedUserId,
        startDate: dateRange.start,
        endDate: dateRange.end,
        timezone,
      });

      // Fetch time tracking data using the V2 API
      const response = await memberSessionsService.fetchTimeTrackingDataV2({
        teamId,
        companyId: activeCompanyId,
        userId: selectedUserId,
        startDate: dateRange.start,
        endDate: dateRange.end,
        timezone,
      });

      if (!response.success) {
        console.error('Failed to fetch time tracking data:', response.error);
        setSessionsData(null);
        setAppUsage({});
        // Clear memberTimeData in Redux on error
        dispatch({
          type: memberActionTypes.SET_STATE,
          payload: {
            memberTimeData: null,
            memberLoading: false,
          },
        });
        return;
      }

      const data = response.data;

      console.log('🟢 [UsersData] API Response Received:', {
        rawData: data,
        totalHours: data.totalHours,
        totalMinutes: data.totalMinutes,
        totalTimeTrackedSeconds: data.totalTimeTrackedSeconds,
        idleHours: data.idleHours,
        idleMinutes: data.idleMinutes,
        productiveHours: data.productiveHours,
        productiveMinutes: data.productiveMinutes,
      });

      // Set sessions data from API response
      const timeData = {
        totalHours: data.totalHours || 0,
        totalMinutes: data.totalMinutes || 0,
        idleHours: data.idleHours || 0,
        idleMinutes: data.idleMinutes || 0,
        productiveHours: data.productiveHours || 0,
        productiveMinutes: data.productiveMinutes || 0,
        screenshotCount: data.screenshotCount || 0,
      };

      console.log('🟢 [UsersData] Processed TimeData:', {
        timeData,
        calculatedTotalMinutes:
          timeData.totalHours * 60 + timeData.totalMinutes,
        calculatedTotalSeconds:
          timeData.totalHours * 3600 + timeData.totalMinutes * 60,
        apiTotalTimeTrackedSeconds: data.totalTimeTrackedSeconds,
        match:
          timeData.totalHours * 3600 + timeData.totalMinutes * 60 ===
          (data.totalTimeTrackedSeconds || 0),
      });

      setSessionsData(timeData);

      // Convert appUsage from seconds to the format expected by the UI
      // The API returns appUsage in seconds, we need to keep it in seconds for display
      const appUsageInSeconds = data.appUsage || {};
      setAppUsage(appUsageInSeconds);

      // Prepare Redux payload
      const reduxPayload = {
        memberTimeData: {
          totalHours: timeData.totalHours,
          totalMinutes: timeData.totalMinutes,
          idleHours: timeData.idleHours,
          idleMinutes: timeData.idleMinutes,
          productiveHours: timeData.productiveHours,
          productiveMinutes: timeData.productiveMinutes,
        },
        memberLoading: false,
      };

      console.log('🟢 [UsersData] Dispatching to Redux:', {
        reduxPayload,
        totalHours: reduxPayload.memberTimeData.totalHours,
        totalMinutes: reduxPayload.memberTimeData.totalMinutes,
        calculatedTotalMinutes:
          reduxPayload.memberTimeData.totalHours * 60 +
          reduxPayload.memberTimeData.totalMinutes,
      });

      // Update Redux state for Timeline component
      console.log('🟢 [UsersData] About to dispatch Redux action:', {
        actionType: memberActionTypes.SET_STATE,
        payload: reduxPayload,
        memberTimeDataKeys: Object.keys(reduxPayload.memberTimeData || {}),
        allValues: reduxPayload.memberTimeData,
      });

      dispatch({
        type: memberActionTypes.SET_STATE,
        payload: reduxPayload,
      });

      console.log('🟢 [UsersData] Redux action dispatched successfully');
    } catch (error) {
      console.error('Error fetching time tracking data:', error);
      setSessionsData(null);
      setAppUsage({});
      // Clear memberTimeData in Redux on error
      dispatch({
        type: memberActionTypes.SET_STATE,
        payload: {
          memberTimeData: null,
          memberLoading: false,
        },
      });
    } finally {
      setLoadingAppUsage(false);
    }
  }, [
    teamId,
    activeCompanyId,
    effectiveEmployeeId,
    dateRange.start,
    dateRange.end,
    selectedTimezone,
    selectedUserId,
    dispatch,
  ]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (
      dateRange.start &&
      dateRange.end &&
      effectiveEmployeeId &&
      activeCompanyId
    ) {
      fetchSessionsAndAppUsage();
    }
  }, [
    dateRange.start,
    dateRange.end,
    effectiveEmployeeId,
    activeCompanyId,
    fetchSessionsAndAppUsage,
  ]);

  // Derive rate from memberDetails (same source as hook)
  useEffect(() => {
    if (!selectedUserId || !memberDetails) {
      setRate('');
      return;
    }
    const member = memberDetails[selectedUserId];
    setRate(member?.rates ?? '');
  }, [memberDetails, selectedUserId]);

  // URL-synced filters — replaces the old "default to self, else first"
  // effect with a URL-aware version (same default rule, now also resolves
  // a deep-linked ?member= and survives refresh/back-forward).
  const selectedMemberOption = useMemo(
    () =>
      selectedUserId
        ? membersOptions.find((m) => m.value === selectedUserId) || null
        : null,
    [selectedUserId, membersOptions]
  );
  const setSelectedMemberOption = useCallback(
    (opt) => setSelectedUserId(opt?.value ?? null),
    []
  );
  // Dropdown's own onChange — distinct from setSelectedMemberOption above
  // (which the URL-hydration/nav-sync hooks also call automatically) since
  // only a *manual* member pick should re-enable "auto-follow this member's
  // own timezone" by resetting the override flags.
  const handleMemberSelectChange = useCallback(
    (opt) => {
      userOverrideTimezoneRef.current = false;
      setIgnoreUrlTimezone(true);
      setSelectedMemberOption(opt);
    },
    [setSelectedMemberOption]
  );
  const handleTimezoneSelectChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone]
  );

  useUrlDateRangeSync({ location, setDateRange });
  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(teamId),
    membersOptions,
    selectedMember: selectedMemberOption,
    setSelectedMember: setSelectedMemberOption,
    currentUserId: auth?.profile?.id,
    getDefaultMember: getDefaultMemberFromOptions,
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(teamId),
    membersOptionsRef,
    setSelectedMember: setSelectedMemberOption,
  });

  const urlWriteParams = useMemo(() => {
    const params = { start: dateRange.start, end: dateRange.end };
    if (selectedUserId) params.member = selectedUserId;
    if (selectedTimezone) params.tz = selectedTimezone;
    return params;
  }, [dateRange.start, dateRange.end, selectedUserId, selectedTimezone]);

  useUrlFilterWrite({
    history,
    location,
    params: urlWriteParams,
    enabled: Boolean(teamId && dateRange?.start && dateRange?.end),
  });

  // Format time from seconds: Xh Ym / Ym / Xs
  const formatTimeFromSeconds = (seconds) => {
    const sec = Math.floor(Number(seconds) || 0);
    if (sec < 60) {
      return `${sec}s`;
    }
    const minutes = Math.floor(sec / 60);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${minutes}m`;
  };

  // Sort app usage by time (descending) and convert to readable names
  const sortedAppUsage = useMemo(() => {
    return Object.entries(appUsage)
      .map(([appPath, seconds]) => {
        const readableName = getAppNameByAppPath(appPath, appNameMapping);
        return {
          appPath, // Keep original path for key
          appName: readableName, // Use readable name for display
          seconds: Number(seconds) || 0,
          formattedTime: formatTimeFromSeconds(Number(seconds) || 0),
        };
      })
      .sort((a, b) => b.seconds - a.seconds);
  }, [appUsage, appNameMapping]);
  return (
    <>
      <div style={{ paddingTop: '5rem!important' }}>
        <Container className="pt-md-0" fluid>
          <Label for="userSelect" className="main">
            Select Member
          </Label>
          {membersError && teamId && (
            <div className="mb-1 text-danger small">
              Failed to load members. {membersErrorObj?.message || ''}{' '}
              <Button
                color="link"
                size="sm"
                className="p-0"
                onClick={() => refetchMembers()}
              >
                Retry
              </Button>
            </div>
          )}
          <Select
            options={membersOptions}
            className="w-100"
            classNamePrefix="rs"
            placeholder={
              teamId
                ? membersLoading
                  ? 'Loading...'
                  : 'Select member'
                : 'Select department first'
            }
            value={
              selectedUserId
                ? membersOptions.find((m) => m.value === selectedUserId) || null
                : null
            }
            onChange={handleMemberSelectChange}
            isClearable
            isDisabled={!teamId || membersLoading || membersError}
            isLoading={membersLoading}
            styles={selectStyles}
            name="userSelect"
          />

          <Row className="mt-3">
            <Col md={6} sm={12} className="mb-3 mb-md-0">
              <Label for="dateRangeSelect" className="main">
                Date Range
              </Label>
              <DateRangePicker
                startDate={moment(dateRange.start)}
                endDate={moment(dateRange.end)}
                onApply={(_event, { startDate, endDate }) => {
                  setDateRange({
                    start: startDate.format('YYYY-MM-DD'),
                    end: endDate.format('YYYY-MM-DD'),
                  });
                }}
              >
                <button
                  type="button"
                  className="w-100 text-left border rounded"
                  style={{
                    height: '38px',
                    cursor: 'pointer',
                    background: '#fff',
                    color: '#495057',
                    padding: '0 12px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {moment(dateRange.start).format('DD MMM YYYY')} -{' '}
                  {moment(dateRange.end).format('DD MMM YYYY')}
                </button>
              </DateRangePicker>
            </Col>
            <Col md={6} sm={12}>
              <Label for="timezoneSelect" className="main">
                Timezone
              </Label>
              <Select
                inputId="timezoneSelect"
                options={timezoneOptions}
                className="w-100"
                classNamePrefix="rs"
                placeholder="Select timezone"
                value={timezoneOptions.find(
                  (tz) => tz.value === selectedTimezone
                )}
                onChange={({ value }) => handleTimezoneSelectChange(value)}
                isSearchable
                isLoading={timezonesLoading}
                styles={selectStyles}
              />
            </Col>
          </Row>
        </Container>
      </div>

      {loadingAppUsage ? (
        <Container className="mt-4" fluid>
          <Card className="shadow">
            <CardBody>
              <div className="text-center my-5">
                <Spinner color="primary" size="lg" />
                <p className="mt-3 text-muted">Loading time tracking data...</p>
              </div>
            </CardBody>
          </Card>
        </Container>
      ) : (
        <>
          <Timeline rates={rate} selectedTimeZone={selectedTimezone} />

          <Container className="mt-4" fluid>
            <Card className="shadow mb-4">
              <CardHeader className="d-flex justify-content-between align-items-start">
                <div>
                  <h4 className="mb-0">Screenshots</h4>
                  <p className="text-muted mb-0">
                    Total screenshots captured from{' '}
                    {moment(dateRange.start).format('MMM DD, YYYY')} to{' '}
                    {moment(dateRange.end).format('MMM DD, YYYY')}
                  </p>
                </div>
                <Link
                  to="/screenshots"
                  className="text-primary small"
                  style={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    marginTop: '0.25rem',
                  }}
                >
                  View →
                </Link>
              </CardHeader>
              <CardBody>
                <div className="text-center">
                  <h3 className="mb-0">{sessionsData?.screenshotCount ?? 0}</h3>
                </div>
              </CardBody>
            </Card>
          </Container>

          <Container className="mt-4" fluid>
            <Card className="shadow">
              <CardHeader className="d-flex justify-content-between align-items-start">
                <div>
                  <h4 className="mb-0">App Usage</h4>
                  <p className="text-muted mb-0">
                    Applications used from{' '}
                    {moment(dateRange.start).format('MMM DD, YYYY')} to{' '}
                    {moment(dateRange.end).format('MMM DD, YYYY')}
                  </p>
                </div>
                <Link
                  to="/app-usage"
                  className="text-primary small"
                  style={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    marginTop: '0.25rem',
                  }}
                >
                  View →
                </Link>
              </CardHeader>
              <CardBody>
                {sortedAppUsage.length === 0 ? (
                  <EmptyState
                    illustration
                    title="No App Usage Data"
                    description="No app usage data available for the selected date range and user."
                  />
                ) : (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Application Name</th>
                        <th>Time Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAppUsage.map((app, index) => (
                        <tr key={app.appPath}>
                          <td>{index + 1}</td>
                          <td>{app.appName}</td>
                          <td>{app.formattedTime}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Container>
        </>
      )}
    </>
  );
};

export default UsersDataPage;
